import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadEmployee } from '../../employees/actions/load-employee';
import { employeeMessageTemplatesForOrgIdSelector } from '../../message-templates/message-template-selectors';
import { loadOrg } from '../../organization/actions/load-org';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeeSelector } from '../../reducers/employee-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import { addEmployeeMessageTemplate } from '../actions/messages/add-employee-message-template';
import { loadEmployeeMessageTemplates } from '../actions/messages/load-employee-message-templates';
import { updateEmployeeMessageTemplate } from '../actions/messages/update-employee-message-template';

import { InlineTemplateList } from '../../message-templates/components/inline-template-list';
import { EmployeeMessageTemplateParser } from '../../message-templates/parsers/employee-message-template-parser';

import { CkEditorNewlineTrimmer } from '../../util/ckeditor-newline-trimmer';
import { MESSAGE_TYPE } from '../../util/message-types';

const mapStateToProps = (state: any, { orgId, employeeId }: InputProps) => ({
  org: orgSelector(state, orgId),
  user: userSelector(state),
  employee: employeeId ? employeeSelector(state, employeeId) : null,
  messageTemplates: employeeMessageTemplatesForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, employeeId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadEmployee: () => employeeId && dispatch(loadEmployee(employeeId)),
    loadEmployeeMessageTemplates: () => dispatch(loadEmployeeMessageTemplates(orgId)),
    addEmployeeMessageTemplate: (template: EmployeeMessageTemplate) =>
      dispatch(addEmployeeMessageTemplate(orgId, template)),
    updateEmployeeMessageTemplate: (template: Partial<EmployeeMessageTemplate>) =>
      dispatch(updateEmployeeMessageTemplate(orgId, template)),
  },
});

type InputProps = {
  orgId: number;
  employeeId?: number | null;
  onSelect: Function;
  templateType: 'MESSAGE' | 'SURVEY';
  messageType: string | null;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeMessageTemplates = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    messageTemplates,
    employee,
    user,
    org,
    templateType,
    employeeId,
    onSelect,
    actions,
    messageType,
  }: Props) => {
    useEffect(() => {
      actions.loadOrg();
      actions.loadEmployee();
      actions.loadEmployeeMessageTemplates();
    });

    if (!org || !messageTemplates) {
      return null;
    }

    const handleSave = (template: EmployeeMessageTemplate) => {
      if (template.employeeMessageTemplateId) {
        actions.updateEmployeeMessageTemplate({ ...template, templateType });
      } else {
        actions.addEmployeeMessageTemplate({ ...template, templateType });
      }
    };

    const handleDelete = (template: EmployeeMessageTemplate) => {
      actions.updateEmployeeMessageTemplate({
        employeeMessageTemplateId: template.employeeMessageTemplateId,
        isActive: false,
      });
    };

    const handleSelect = (messageBody: string) => {
      let newTemplate = messageBody;
      // There's not really a way to get messageType to be null here - it gets updated as soon as the editor is rendered
      // We store our templates with HTML intact, so if we go to use them for a text, we need to remove the <br>'s first,
      //    because otherwise we're going to double them when we inject <br>'s in the ui-rich-text-editor
      // This same thing is done in applicant-templates and will need to be updated if this is changed
      if (messageType && messageType === MESSAGE_TYPE.TEXT) {
        newTemplate = CkEditorNewlineTrimmer.trimBrs(newTemplate);
      }
      const shouldParseTemplate = Boolean(employeeId);
      if (shouldParseTemplate) {
        const parser = new EmployeeMessageTemplateParser({ employee, user, org });
        onSelect(parser.parse(newTemplate));
      } else {
        onSelect(newTemplate);
      }
    };

    const preventMessageParsing = !employeeId;
    const parser = new EmployeeMessageTemplateParser({ employee, user, org });
    const messageTemplatesForType = messageTemplates.filter((t) => t.templateType === templateType);

    return (
      <InlineTemplateList
        templates={messageTemplatesForType}
        parser={parser}
        preventMessageParsing={preventMessageParsing}
        onSelect={handleSelect}
        onSave={handleSave}
        onDelete={handleDelete}
        addButtonText={templateType === 'SURVEY' ? 'Add Saved Survey +' : 'Add Saved Message +'}
        recipientType="employee"
        templateType="message"
      />
    );
  },
);
